import { useEffect, useRef, useState } from 'react';
import { useSearchParams, useParams, useNavigate } from 'react-router-dom';
import ChatWidget from '../components/ChatWidget';
import { ContentDetailWorkspace } from '../components/content/ContentDetailWorkspace';
import { QueueItem } from '../components/content/QueueItem';
import type { PipelineState } from '../components/content/QueueItem';
import { ContentTabs } from '../components/content/ContentTabs';
import { GenerationProgress } from '../components/content/GenerationProgress';
import { AgentBanner } from '../components/content/AgentBanner';
import { PipelineHealthCard } from '../components/content/PipelineHealthCard';
import { VideoProgressBanner } from '../components/content/VideoProgressBanner';
import { NotesModal } from '../components/NotesModal';
import { OutlineCreationModal } from '../components/OutlineCreationModal';
import { ReelStudioModal } from '../components/ReelStudioModal';
import { PublishModal } from '../components/PublishModal';
import { CarouselGenerationModeModal } from '../components/CarouselGenerationModeModal';
import { CarouselRegenerateModal } from '../components/CarouselRegenerateModal';
import ConfirmModal from '../components/scheduler/ConfirmModal';
import { Badge, Button, LoadingOverlay, statusVariant, useToast } from '../components/ui';
import {
  getOutlines,
  getOutline,
  createOutline,
  updateOutline,
  deleteOutline,
  archiveOutline,
  unarchiveOutline,
  generateOutlines,
  generateContentItem,
  regenerateContentItem,
  getJobStatus,
  getContentItems,
  getContentItemByOutline,
  updateContentItem,
  getPillars,
  getPlans,
  generateImages,
  generateVideos,
  publishContentItem,
  markContentItemManuallyUploaded,
  refreshInstagramPostAnalytics,
  getSchedulerTasksForOutline,
  createMedia,
  updateMedia,
  deleteMedia,
  reorderMedia,
  uploadMediaFile,
  getBillingSubscription,
} from '../api';
import type { ActiveJob } from '../api';
import { pollJobStatus } from '../utils/jobPolling';
import type {
  ContentItem,
  ContentOutline,
  ContentPillar,
  ContentPlan,
  JobStatus,
  ScheduledTask,
  Workspace,
  PageKey,
} from '../types';
import './ContentPage.css';

const CONTENT_JOB_ACTIONS = new Set(['CREATE_CONTENT', 'REGENERATE_CONTENT']);
const MEDIA_JOB_ACTIONS = new Set(['GENERATE_IMAGES', 'GENERATE_VIDEOS']);

type ContentPageProps = {
  workspaceId: number;
  workspace: Workspace;
  onNavigate?: (_page: PageKey) => void;
  activeJobs?: ActiveJob[];
};

type ContentTab = 'active' | 'archived';

/** How long (ms) the agent message banner stays visible before auto-dismissing. */
const AGENT_MESSAGE_DISMISS_DELAY_MS = 12_000;

/**
 * Pull the agent's summary message out of a completed job's output object.
 * Handles both the raw JSON string form (`output.result`) and the pre-parsed form
 * (`output.data.message`).
 */
const extractJobMessage = (output: unknown): string | null => {
  const o = output as { result?: string; data?: { message?: string } } | undefined;
  if (!o) return null;
  if (o.result) {
    try {
      const parsed =
        typeof o.result === 'string'
          ? (JSON.parse(o.result) as { message?: string })
          : (o.result as { message?: string });
      if (parsed?.message) return parsed.message;
    } catch {
      // ignore parse errors
    }
  }
  return o.data?.message ?? null;
};

function ContentPage({ workspaceId, workspace, onNavigate, activeJobs = [] }: ContentPageProps) {
  const { toast } = useToast();
  const navigate = useNavigate();
  const { outlineId: outlineIdParam } = useParams<{ outlineId?: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const [outlines, setOutlines] = useState<ContentOutline[]>([]);
  const [pillars, setPillars] = useState<ContentPillar[]>([]);
  const [plans, setPlans] = useState<ContentPlan[]>([]);
  const [allowedFormats, setAllowedFormats] = useState<string[]>(['image', 'carousel', 'video']);
  const [contentItems, setContentItems] = useState<ContentItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [error, setError] = useState('');
  const [selectedOutline, setSelectedOutline] = useState<ContentOutline | null>(null);
  const [contentItem, setContentItem] = useState<ContentItem | null>(null);
  const [generatingContent, setGeneratingContent] = useState(false);
  // Set of media IDs currently being regenerated as independent per-slide jobs.
  // Allows e.g. clicking "Re-render slide 2" and "Re-render slide 4" concurrently.
  const [generatingMediaIds, setGeneratingMediaIds] = useState<Set<number>>(() => new Set());
  // True while a multi-slide job (initial generate-all or regenerate-all) is in flight.
  // Mutually exclusive with per-slide jobs to avoid races on the same media row.
  const [multiSlideJobInFlight, setMultiSlideJobInFlight] = useState(false);
  // Distinguishes regen-all from initial-gen-all so the UI can show "Re-rendering all…".
  const [regeneratingAllMedia, setRegeneratingAllMedia] = useState(false);
  const [videoJobStatus, setVideoJobStatus] = useState<JobStatus | null>(null);
  const [showCreationModal, setShowCreationModal] = useState(false);
  const [showReelStudio, setShowReelStudio] = useState(false);
  const [creationModalInitialMode, setCreationModalInitialMode] = useState<
    'quick' | 'specific' | 'manual'
  >('quick');
  const [agentMessage, setAgentMessage] = useState<string | null>(null);
  const [agentMessageType, setAgentMessageType] = useState<'outline' | 'content'>('outline');
  const agentMessageTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [showPublishModal, setShowPublishModal] = useState(false);
  const [publishingContentId, setPublishingContentId] = useState<number | null>(null);
  const [refreshingInstagramMetrics, setRefreshingInstagramMetrics] = useState(false);
  const [currentMediaIndex, setCurrentMediaIndex] = useState(0);
  const pageSize = 50;
  const [totalCount, setTotalCount] = useState(0);
  const [activeCount, setActiveCount] = useState(0);
  const [archivedCount, setArchivedCount] = useState(0);
  const sortField: 'id' | 'publish_at' = 'id';
  const sortOrder: 'asc' | 'desc' = 'desc';
  const cleanupOutlinesRef = useRef<null | (() => void)>(null);
  const cleanupContentRef = useRef<null | (() => void)>(null);

  // Notes modal state (replaces window.prompt for generate/regenerate)
  const [notesModal, setNotesModal] = useState<{
    mode: 'generate' | 'regenerate';
    targetId: number;
  } | null>(null);

  /** Show a message from the AI agent, auto-dismissing after AGENT_MESSAGE_DISMISS_DELAY_MS. */
  const showAgentMessage = (message: string, type: 'outline' | 'content') => {
    if (agentMessageTimerRef.current) clearTimeout(agentMessageTimerRef.current);
    setAgentMessage(message);
    setAgentMessageType(type);
    agentMessageTimerRef.current = setTimeout(
      () => setAgentMessage(null),
      AGENT_MESSAGE_DISMISS_DELAY_MS
    );
  };

  // URL-derived state — persists selected outline and pagination in the browser URL
  const currentPage = Math.max(1, parseInt(searchParams.get('page') || '1', 10));
  const currentTab: ContentTab = searchParams.get('view') === 'archived' ? 'archived' : 'active';
  const urlItemId = outlineIdParam ? parseInt(outlineIdParam, 10) : null;
  const requestedTab = searchParams.get('tab');
  const initialWorkspaceTab: 'preview' | 'edit' | 'meta' =
    requestedTab === 'preview' || requestedTab === 'edit' || requestedTab === 'meta'
      ? requestedTab
      : 'preview';

  const setCurrentPage = (updater: number | ((_: number) => number)) => {
    const next = typeof updater === 'function' ? updater(currentPage) : updater;
    setSearchParams(
      (_prev) => {
        const p = new URLSearchParams(_prev);
        if (next <= 1) p.delete('page');
        else p.set('page', String(next));
        return p;
      },
      { replace: true }
    );
  };

  const setCurrentTab = (tab: ContentTab) => {
    setSearchParams(
      (prev) => {
        const p = new URLSearchParams(prev);
        if (tab === 'active') p.delete('view');
        else p.set('view', 'archived');
        p.delete('page');
        return p;
      },
      { replace: true }
    );
  };

  /** Selects an outline and navigates to /content/:id for browser back/forward support. */
  const selectOutline = (outline: ContentOutline | null) => {
    setSelectedOutline(outline);
    setCurrentMediaIndex(0);
    if (outline) {
      navigate(`/content/${outline.id}`);
    } else {
      navigate('/content');
    }
  };

  // Editable caption state
  const [editingCaption, setEditingCaption] = useState(false);
  const [captionDraft, setCaptionDraft] = useState('');
  const [savingCaption, setSavingCaption] = useState(false);

  // Editable per-media prompts state  (key = media id or index)
  const [editingPromptId, setEditingPromptId] = useState<number | null>(null);
  const [promptDraft, setPromptDraft] = useState('');
  const [savingPrompt, setSavingPrompt] = useState(false);

  // Editable hashtags state
  const [editingHashtags, setEditingHashtags] = useState(false);
  const [hashtagsDraft, setHashtagsDraft] = useState('');
  const [savingHashtags, setSavingHashtags] = useState(false);

  // Delete confirmation
  const [confirmDelete, setConfirmDelete] = useState(false);

  // Scheduler tasks for selected outline
  const [schedulerTasks, setSchedulerTasks] = useState<ScheduledTask[]>([]);
  const [savingOutlineMeta, setSavingOutlineMeta] = useState(false);

  // Image generation mode preference (persisted in component state)
  const [generationMode, setGenerationMode] = useState<'sequential_context' | 'parallel'>(
    'sequential_context'
  );
  const [carouselModeModal, setCarouselModeModal] = useState<{
    contentItemId: number;
    options?: {
      mediaIndex?: number;
      mediaId?: number;
      regenerateAll?: boolean;
      contentFormat?: string;
      generationModeOverride?: 'sequential_context' | 'parallel';
    };
  } | null>(null);
  // Unified scope+mode picker for regen-all on a carousel.
  const [carouselRegenModal, setCarouselRegenModal] = useState<{
    contentItemId: number;
  } | null>(null);

  // "A run is already in flight for this post" → offer to override & restart.
  // Set when a (re)generation request comes back 409 already_running.
  const [overridePrompt, setOverridePrompt] = useState<{
    message: string;
    onConfirm: () => void;
  } | null>(null);

  const formatDate = (value?: string) => {
    if (!value) {
      return 'Unknown date';
    }
    const parsed = new Date(value);
    if (Number.isNaN(parsed.getTime())) {
      return 'Unknown date';
    }
    return parsed.toLocaleDateString();
  };

  const getPlanName = (planId: number | null | undefined): string => {
    if (!planId) return '';
    return plans.find((p) => p.id === planId)?.name || '';
  };

  const getPillarName = (pillarId: number | null | undefined): string => {
    if (!pillarId) return '';
    return pillars.find((p) => p.id === pillarId)?.name || '';
  };

  const getContentItemForOutline = (outlineId: number): ContentItem | undefined => {
    return contentItems.find((i) => i.outline === outlineId);
  };

  const hasMediaGenerated = (item: ContentItem | undefined): boolean => {
    if (!item || !item.media) return false;
    return item.media.length > 0 && item.media.some((m) => m.media_url);
  };

  /** Returns true if there is a currently queued/running content-gen job for this outline. */
  const hasActiveContentJobForOutline = (outlineId: number): boolean =>
    activeJobs.some(
      (j) =>
        j.related_object_type === 'content_outline' &&
        j.related_object_id === String(outlineId) &&
        CONTENT_JOB_ACTIONS.has(j.action)
    );

  /** Returns true if there is a currently queued/running media-gen job for this outline. */
  const hasActiveMediaJobForOutline = (outlineId: number): boolean =>
    activeJobs.some(
      (j) =>
        j.related_object_type === 'content_outline' &&
        j.related_object_id === String(outlineId) &&
        MEDIA_JOB_ACTIONS.has(j.action)
    );

  const videoProgressBanner = <VideoProgressBanner videoJobStatus={videoJobStatus} />;

  useEffect(() => {
    loadData();

    return () => {
      if (cleanupOutlinesRef.current) {
        cleanupOutlinesRef.current();
      }
      if (cleanupContentRef.current) {
        cleanupContentRef.current();
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [workspaceId, currentPage, currentTab]);

  // Restore selected outline from URL param when outlines are (re-)loaded, or when URL changes
  useEffect(() => {
    let isCancelled = false;

    const restoreSelection = async () => {
      if (!urlItemId) {
        if (selectedOutline !== null) setSelectedOutline(null);
        return;
      }

      const found = outlines.find((o) => o.id === urlItemId);
      if (found) {
        if (found.id !== selectedOutline?.id) setSelectedOutline(found);
        return;
      }

      // Fallback for direct links from calendar to older outlines that are not on the current page.
      try {
        const fetched = await getOutline(urlItemId);
        if (!isCancelled) setSelectedOutline(fetched);
      } catch {
        if (!isCancelled && selectedOutline !== null) setSelectedOutline(null);
      }
    };

    void restoreSelection();

    return () => {
      isCancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [urlItemId, outlines]);

  useEffect(() => {
    if (!selectedOutline) {
      setContentItem(null);
      return;
    }

    const cached = getContentItemForOutline(selectedOutline.id);
    if (cached) {
      setContentItem(cached);
      return;
    }

    // Not in the loaded page (the list endpoint only returns the newest page),
    // so fetch the full item — caption, media, hashtags — by outline on demand.
    let cancelled = false;
    setContentItem(null);
    getContentItemByOutline(selectedOutline.id)
      .then((item) => {
        if (cancelled || !item) return;
        setContentItem(item);
        // Cache it so the list/cards and re-selection can reuse it.
        setContentItems((prev) =>
          prev.some((i) => i.id === item.id) ? prev : [...prev, item]
        );
      })
      .catch(() => {
        // Leave contentItem null on failure; the detail view shows the outline.
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedOutline, contentItems]);

  // Load scheduler tasks for the selected outline
  useEffect(() => {
    if (!selectedOutline) {
      setSchedulerTasks([]);
      return;
    }
    getSchedulerTasksForOutline(workspaceId, selectedOutline.id)
      .then((data) => setSchedulerTasks(data.results || []))
      .catch(() => setSchedulerTasks([]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedOutline?.id]);

  const handleSaveOutlineMeta = async (patch: {
    title: string;
    summary: string;
    content_format: string;
    publish_at?: string | null;
  }) => {
    if (!selectedOutline) return;

    setSavingOutlineMeta(true);
    try {
      const updated = await updateOutline(selectedOutline.id, patch);
      setSelectedOutline(updated);
      setOutlines((prev) => prev.map((o) => (o.id === updated.id ? updated : o)));

      const tasksData = await getSchedulerTasksForOutline(workspaceId, updated.id);
      setSchedulerTasks(tasksData.results || []);
      toast('Post setup saved', 'success');
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to save post setup';
      toast(message, 'error');
      throw err;
    } finally {
      setSavingOutlineMeta(false);
    }
  };

  const loadData = async () => {
    try {
      const orderingField = sortOrder === 'desc' ? `-${sortField}` : sortField;
      const outlinesFilterArchived = currentTab === 'archived';
      const [
        outlinesData,
        activeOutlinesData,
        archivedOutlinesData,
        pillarsData,
        plansData,
        itemsData,
        subscription,
      ] = await Promise.all([
        getOutlines({
          page: currentPage,
          page_size: pageSize,
          ordering: orderingField,
          archived: outlinesFilterArchived,
        }),
        getOutlines({ page: 1, page_size: 1, archived: false }),
        getOutlines({ page: 1, page_size: 1, archived: true }),
        getPillars(),
        getPlans(),
        getContentItems(),
        getBillingSubscription(workspaceId).catch(() => null),
      ]);
      setOutlines(outlinesData.results || []);
      setPillars(pillarsData.results || []);
      setPlans(plansData.results || []);
      setContentItems(itemsData.results || []);
      const entitled = ((subscription?.entitlements?.formats as string[] | undefined) ?? []).filter(
        (f) => ['image', 'carousel', 'video'].includes(f)
      );
      setAllowedFormats(entitled.length > 0 ? entitled : ['image', 'carousel', 'video']);
      setTotalCount(outlinesData.count || 0);
      setActiveCount(activeOutlinesData.count || 0);
      setArchivedCount(archivedOutlinesData.count || 0);
    } catch (err) {
      console.error('Failed to load data:', err);
      setError('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateOwn = async (data: {
    title: string;
    summary: string;
    content_format: string;
    pillar: number | null;
    plan: number | null;
    publish_at: string | null;
  }) => {
    setError('');
    try {
      const newOutline = await createOutline(workspaceId, {
        title: data.title,
        summary: data.summary,
        content_format: data.content_format,
        pillar: data.pillar,
        plan: data.plan,
        publish_at: data.publish_at,
        status: 'draft',
      });
      setOutlines((prev) => [newOutline, ...prev]);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Unknown error';
      throw new Error(message);
    }
  };

  const handleCreateWithAI = async (data: {
    plan: number | null;
    notes: string;
    outlines: Array<{
      pillar: number | null;
      content_format: string;
      forced_publish_at: string | null;
      force_date_instruction: string | null;
    }>;
  }) => {
    setGenerating(true);
    setError('');
    try {
      if (cleanupOutlinesRef.current) {
        cleanupOutlinesRef.current();
      }

      // Quick mode sends no per-post specs — the agent infers count and dates.
      // Specific mode sends one outline_request per card.
      const requestData: Record<string, unknown> = { notes: data.notes };
      if (data.outlines.length > 0) {
        requestData.outline_requests = data.outlines.map((outline) => ({
          pillar_id: outline.pillar,
          content_format: outline.content_format,
          forced_publish_at: outline.forced_publish_at,
          force_date_instruction: outline.force_date_instruction,
        }));
      }
      if (data.plan) {
        requestData.plan_id = data.plan;
      }

      const jobResponse = await generateOutlines(workspaceId, requestData);
      const jobId = jobResponse.job_id;

      cleanupOutlinesRef.current = pollJobStatus(
        jobId,
        getJobStatus,
        async (jobResult) => {
          setGenerating(false);
          setShowCreationModal(false);
          const msg = extractJobMessage(jobResult.output);
          if (msg) showAgentMessage(msg, 'outline');
          await loadData();
          cleanupOutlinesRef.current = null;
        },
        (errorMessage) => {
          setGenerating(false);
          setError(errorMessage);
          cleanupOutlinesRef.current = null;
        }
      );
    } catch (err) {
      setGenerating(false);
      const message = err instanceof Error ? err.message : 'Unknown error';
      throw new Error(message);
    }
  };

  const handleApprove = async (id: number) => {
    try {
      const updated = await updateOutline(id, { status: 'approved' });
      setOutlines((prev) => prev.map((o) => (o.id === id ? updated : o)));
      setSelectedOutline(updated);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Unknown error';
      setError(message);
    }
  };

  const handleApproveContentItem = async (id: number) => {
    try {
      const updated = await updateContentItem(id, { status: 'approved' });
      setContentItems((prev) => prev.map((i) => (i.id === id ? updated : i)));
      if (contentItem?.id === id) {
        setContentItem(updated);
      }
      toast('Content approved — ready for media generation.', 'success');
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Unknown error';
      setError(message);
    }
  };

  const handleDelete = async (id: number) => {
    try {
      await deleteOutline(id);
      setOutlines((prev) => prev.filter((o) => o.id !== id));
      setContentItems((prev) => prev.filter((i) => i.outline !== id));
      if (selectedOutline?.id === id) {
        setSelectedOutline(null);
        navigate('/content');
      }
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Unknown error';
      setError(message);
    }
  };

  const handleArchive = async (outlineId: number) => {
    try {
      await archiveOutline(outlineId);
      if (selectedOutline?.id === outlineId) {
        setSelectedOutline(null);
        navigate('/content');
      }
      await loadData();
      toast('Post archived', 'success');
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to archive post';
      toast(message, 'error');
    }
  };

  const handleUnarchive = async (outlineId: number) => {
    try {
      await unarchiveOutline(outlineId);
      if (selectedOutline?.id === outlineId) {
        setSelectedOutline(null);
        navigate('/content');
      }
      await loadData();
      toast('Post restored to active pipeline', 'success');
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to restore post';
      toast(message, 'error');
    }
  };

  const handlePublish = async () => {
    if (!publishingContentId) return;

    try {
      const result = await publishContentItem(publishingContentId);
      // Update the content item status
      setContentItems((prev) =>
        prev.map((item) =>
          item.id === publishingContentId
            ? {
                ...item,
                status: 'published',
                instagram_post_id: result.instagram_post_id,
                published_via: result.published_via,
                published_at: result.published_at,
              }
            : item
        )
      );
      // Update the selected content item
      if (contentItem?.id === publishingContentId) {
        setContentItem({
          ...contentItem,
          status: 'published',
          instagram_post_id: result.instagram_post_id,
          published_via: result.published_via,
          published_at: result.published_at,
        });
      }
      setShowPublishModal(false);
      setPublishingContentId(null);
      toast('Content published to Instagram! 🎉', 'success');
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to publish content';
      toast(message, 'error');
      throw err;
    }
  };

  const handleMarkManuallyUploaded = async (instagramPostId?: string) => {
    if (!publishingContentId) return;

    try {
      const result = await markContentItemManuallyUploaded(publishingContentId, instagramPostId);

      setContentItems((prev) =>
        prev.map((item) =>
          item.id === publishingContentId
            ? {
                ...item,
                status: 'published',
                instagram_post_id: result.instagram_post_id,
                published_via: result.published_via,
                published_at: result.published_at,
              }
            : item
        )
      );

      if (contentItem?.id === publishingContentId) {
        setContentItem({
          ...contentItem,
          status: 'published',
          instagram_post_id: result.instagram_post_id,
          published_via: result.published_via,
          published_at: result.published_at,
        });
      }

      setShowPublishModal(false);
      setPublishingContentId(null);
      toast('Marked as manually uploaded. Scheduler updated.', 'success');
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to mark as manually uploaded';
      toast(message, 'error');
      throw err;
    }
  };

  const handleOpenPublishModal = (contentId: number) => {
    setPublishingContentId(contentId);
    setShowPublishModal(true);
  };

  const handleRefreshInstagramMetrics = async () => {
    if (!contentItem?.instagram_post_id) {
      toast('No Instagram post id available for this content item.', 'error');
      return;
    }

    setRefreshingInstagramMetrics(true);
    try {
      const result = await refreshInstagramPostAnalytics(
        workspaceId,
        contentItem.instagram_post_id
      );
      const updatedItem: ContentItem = {
        ...contentItem,
        engagement_likes: result.likes,
        engagement_comments: result.comments,
      };
      setContentItem(updatedItem);
      setContentItems((prev) => prev.map((i) => (i.id === updatedItem.id ? updatedItem : i)));
      toast('Instagram stats refreshed.', 'success');
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to refresh Instagram stats';
      toast(message, 'error');
    } finally {
      setRefreshingInstagramMetrics(false);
    }
  };

  const handleGoToSettings = () => {
    setShowPublishModal(false);
    setPublishingContentId(null);
    if (onNavigate) {
      onNavigate('settings');
    }
  };

  const openCreationModal = (mode: 'quick' | 'specific' | 'manual' = 'quick') => {
    setCreationModalInitialMode(mode);
    setShowCreationModal(true);
  };

  const handleGenerateContentItem = (outlineId: number) => {
    setNotesModal({ mode: 'generate', targetId: outlineId });
  };

  const handleRegenerateContentItem = (contentItemId: number) => {
    setNotesModal({ mode: 'regenerate', targetId: contentItemId });
  };

  const executeGenerateContent = async (
    outlineId: number,
    notes: string,
    force = false
  ) => {
    setGeneratingContent(true);
    setError('');
    try {
      const sourceOutline = outlines.find((o) => o.id === outlineId);
      if (
        sourceOutline &&
        (sourceOutline.status === 'draft' || sourceOutline.status === 'rejected')
      ) {
        const approvedOutline = await updateOutline(outlineId, { status: 'approved' });
        setOutlines((prev) => prev.map((o) => (o.id === outlineId ? approvedOutline : o)));
        setSelectedOutline((prev) => (prev?.id === outlineId ? approvedOutline : prev));
      }

      if (cleanupContentRef.current) {
        cleanupContentRef.current();
      }

      const jobResponse = await generateContentItem(workspaceId, {
        outline_id: outlineId,
        notes,
        ...(force && { force: true }),
      });
      const jobId = jobResponse.job_id;

      cleanupContentRef.current = pollJobStatus(
        jobId,
        getJobStatus,
        async (jobResult) => {
          setGeneratingContent(false);
          const msg = extractJobMessage(jobResult.output);
          if (msg) showAgentMessage(msg, 'content');
          await loadData();
          cleanupContentRef.current = null;
        },
        (errorMessage) => {
          setGeneratingContent(false);
          setError(errorMessage);
          cleanupContentRef.current = null;
        }
      );
    } catch (err) {
      setGeneratingContent(false);
      if ((err as { alreadyRunning?: boolean })?.alreadyRunning) {
        setOverridePrompt({
          message:
            'Content for this post is already being generated — it may have been started automatically by the scheduler. Cancel that run and start over?',
          onConfirm: () => {
            setOverridePrompt(null);
            void executeGenerateContent(outlineId, notes, true);
          },
        });
        return;
      }
      const message = err instanceof Error ? err.message : 'Unknown error';
      setError(message);
    }
  };

  const executeRegenerateContent = async (
    contentItemId: number,
    notes: string,
    force = false
  ) => {
    setGeneratingContent(true);
    setError('');
    try {
      if (cleanupContentRef.current) {
        cleanupContentRef.current();
      }

      const jobResponse = await regenerateContentItem(workspaceId, {
        content_item_id: contentItemId,
        notes,
        ...(force && { force: true }),
      });
      const jobId = jobResponse.job_id;

      cleanupContentRef.current = pollJobStatus(
        jobId,
        getJobStatus,
        async (jobResult) => {
          setGeneratingContent(false);
          const msg = extractJobMessage(jobResult.output);
          if (msg) showAgentMessage(msg, 'content');
          await loadData();
          cleanupContentRef.current = null;
        },
        (errorMessage) => {
          setGeneratingContent(false);
          setError(errorMessage);
          cleanupContentRef.current = null;
        }
      );
    } catch (err) {
      setGeneratingContent(false);
      if ((err as { alreadyRunning?: boolean })?.alreadyRunning) {
        setOverridePrompt({
          message:
            'Content for this post is already being generated — it may have been started automatically by the scheduler. Cancel that run and start over?',
          onConfirm: () => {
            setOverridePrompt(null);
            void executeRegenerateContent(contentItemId, notes, true);
          },
        });
        return;
      }
      const message = err instanceof Error ? err.message : 'Unknown error';
      setError(message);
    }
  };

  const handleNotesConfirm = async (notes: string) => {
    if (!notesModal) return;
    const { mode, targetId } = notesModal;
    setNotesModal(null);
    if (mode === 'generate') {
      await executeGenerateContent(targetId, notes);
    } else {
      await executeRegenerateContent(targetId, notes);
    }
  };

  const handleGenerateMedia = async (
    contentItemId: number,
    options?: {
      mediaIndex?: number;
      mediaId?: number;
      regenerateAll?: boolean;
      contentFormat?: string;
      /** Override the workspace-level mode preference for this specific call */
      generationModeOverride?: 'sequential_context' | 'parallel';
      /** Cancel any run already in flight for this post and start over. */
      force?: boolean;
    }
  ) => {
    const mediaIndex = options?.mediaIndex;
    const mediaId = options?.mediaId;
    const regenerateAll = Boolean(options?.regenerateAll);
    const contentFormat =
      options?.contentFormat ??
      contentItems.find((item) => item.id === contentItemId)?.content_format;
    const isSingleSlide = mediaId !== undefined || mediaIndex !== undefined;
    const isMultiSlide = !isSingleSlide;

    // For carousel multi-slide actions, route through a modal first unless mode is already chosen.
    if (contentFormat === 'carousel' && isMultiSlide && !options?.generationModeOverride) {
      if (regenerateAll) {
        // Re-render all → unified scope + mode picker
        setCarouselRegenModal({ contentItemId });
      } else {
        // Initial Generate All → mode-only picker
        setCarouselModeModal({ contentItemId, options });
      }
      return;
    }

    // Concurrency rules: protect against racing on the same media rows.
    if (isMultiSlide && generatingMediaIds.size > 0) {
      setError(
        'A per-slide regeneration is still running. Wait for it to finish before regenerating the whole carousel.'
      );
      return;
    }
    if (isSingleSlide && multiSlideJobInFlight) {
      setError(
        'A full-carousel generation is running. Wait until it finishes before regenerating a single slide.'
      );
      return;
    }
    if (mediaId !== undefined && generatingMediaIds.has(mediaId)) {
      // Already running for this slide; ignore double-click.
      return;
    }

    const effectiveMode = isSingleSlide
      ? 'sequential_context'
      : (options?.generationModeOverride ?? generationMode);

    // Mark in-flight before kicking off the request.
    if (isMultiSlide) {
      setMultiSlideJobInFlight(true);
      setRegeneratingAllMedia(regenerateAll);
    } else if (mediaId !== undefined) {
      setGeneratingMediaIds((prev) => {
        const next = new Set(prev);
        next.add(mediaId);
        return next;
      });
    }

    const clearInFlight = () => {
      if (isMultiSlide) {
        setMultiSlideJobInFlight(false);
        setRegeneratingAllMedia(false);
      } else if (mediaId !== undefined) {
        setGeneratingMediaIds((prev) => {
          if (!prev.has(mediaId)) return prev;
          const next = new Set(prev);
          next.delete(mediaId);
          return next;
        });
      }
    };

    setError('');
    setVideoJobStatus(null);
    try {
      const force = Boolean(options?.force);
      const isVideoContent = contentFormat === 'video';
      const jobResponse = isVideoContent
        ? await generateVideos(workspaceId, {
            content_item_id: contentItemId,
            ...(mediaId !== undefined && { media_id: mediaId }),
            ...(regenerateAll && { regenerate_all: true }),
            ...(force && { force: true }),
          })
        : await generateImages(workspaceId, {
            content_item_id: contentItemId,
            ...(mediaId !== undefined && { media_id: mediaId }),
            ...(mediaIndex !== undefined && { media_index: mediaIndex }),
            ...(regenerateAll && { regenerate_all: true }),
            generation_mode: effectiveMode,
            ...(force && { force: true }),
          });
      const jobId = jobResponse.job_id;

      if (isVideoContent) {
        setVideoJobStatus({
          job_id: jobId,
          status: 'queued',
          progress: 0,
        });
      }

      pollJobStatus(
        jobId,
        getJobStatus,
        async () => {
          clearInFlight();
          setVideoJobStatus(null);
          await loadData();
        },
        (errorMessage) => {
          clearInFlight();
          setVideoJobStatus(null);
          setError(errorMessage);
        },
        isVideoContent
          ? (status) => {
              setVideoJobStatus(status);
            }
          : undefined,
        isVideoContent ? 0 : 300000
      );
    } catch (err) {
      clearInFlight();
      setVideoJobStatus(null);
      if ((err as { alreadyRunning?: boolean })?.alreadyRunning) {
        setOverridePrompt({
          message:
            'Media for this post is already being generated — it may have been started automatically by the scheduler. Cancel that run and start over?',
          onConfirm: () => {
            setOverridePrompt(null);
            void handleGenerateMedia(contentItemId, { ...options, force: true });
          },
        });
        return;
      }
      const message = err instanceof Error ? err.message : 'Unknown error';
      setError(message);
    }
  };

  const [togglingCta, setTogglingCta] = useState(false);
  const handleToggleCtaImage = async (value: boolean | null) => {
    if (!contentItem) return;
    setTogglingCta(true);
    try {
      const updated = await updateContentItem(contentItem.id, { include_cta_image: value });
      setContentItem(updated);
      setContentItems((prev) => prev.map((i) => (i.id === updated.id ? updated : i)));
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to update sign-off setting';
      toast(message, 'error');
    } finally {
      setTogglingCta(false);
    }
  };

  const handleSaveCaption = async () => {
    if (!contentItem) return;
    setSavingCaption(true);
    try {
      const updated = await updateContentItem(contentItem.id, { caption: captionDraft });
      setContentItem(updated);
      setContentItems((prev) => prev.map((i) => (i.id === updated.id ? updated : i)));
      setEditingCaption(false);
      toast('Caption saved', 'success');
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to save caption';
      toast(message, 'error');
    } finally {
      setSavingCaption(false);
    }
  };

  const handleSaveHashtags = async () => {
    if (!contentItem) return;
    setSavingHashtags(true);
    try {
      const updated = await updateContentItem(contentItem.id, { hashtags: hashtagsDraft });
      setContentItem(updated);
      setContentItems((prev) => prev.map((i) => (i.id === updated.id ? updated : i)));
      setEditingHashtags(false);
      toast('Hashtags saved', 'success');
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to save hashtags';
      toast(message, 'error');
    } finally {
      setSavingHashtags(false);
    }
  };

  const handleSaveMediaPrompt = async (mediaId: number, newPrompt: string) => {
    if (!contentItem || !contentItem.media) return;
    setSavingPrompt(true);
    try {
      const updatedMedia = await updateMedia(mediaId, { prompt: newPrompt });
      const updatedItem = {
        ...contentItem,
        media: contentItem.media.map((m) => (m.id === mediaId ? { ...m, ...updatedMedia } : m)),
      };
      setContentItem(updatedItem);
      setContentItems((prev) => prev.map((i) => (i.id === updatedItem.id ? updatedItem : i)));
      setEditingPromptId(null);
      toast('Prompt saved', 'success');
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to save prompt';
      toast(message, 'error');
    } finally {
      setSavingPrompt(false);
    }
  };

  const handleToggleHideMedia = async (mediaId: number, hidden: boolean) => {
    if (!contentItem || !contentItem.media) return;
    try {
      const updatedMedia = await updateMedia(mediaId, { is_hidden: hidden });
      const updatedItem = {
        ...contentItem,
        media: contentItem.media.map((m) => (m.id === mediaId ? { ...m, ...updatedMedia } : m)),
      };
      setContentItem(updatedItem);
      setContentItems((prev) => prev.map((i) => (i.id === updatedItem.id ? updatedItem : i)));
      toast(hidden ? 'Slide hidden from post' : 'Slide shown in post', 'success');
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to update media';
      toast(message, 'error');
    }
  };

  const handleDeleteMedia = async (mediaId: number) => {
    if (!contentItem || !contentItem.media) return;
    try {
      await deleteMedia(mediaId);
      const remaining = contentItem.media.filter((m) => m.id !== mediaId);
      const updatedItem = { ...contentItem, media: remaining };
      setContentItem(updatedItem);
      setContentItems((prev) => prev.map((i) => (i.id === updatedItem.id ? updatedItem : i)));
      // If deleted current slide, adjust index
      if (currentMediaIndex >= remaining.length && remaining.length > 0) {
        setCurrentMediaIndex(remaining.length - 1);
      } else if (remaining.length === 0) {
        setCurrentMediaIndex(0);
      }
      toast('Slide deleted', 'success');
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to delete slide';
      toast(message, 'error');
    }
  };

  const handleAddMediaPrompt = async (prompt: string) => {
    if (!contentItem) return;
    try {
      const newMedia = await createMedia(contentItem.id, {
        prompt,
        media_type: 'image',
      });
      const updatedItem = {
        ...contentItem,
        media: [...(contentItem.media ?? []), newMedia],
      };
      setContentItem(updatedItem);
      setContentItems((prev) => prev.map((i) => (i.id === updatedItem.id ? updatedItem : i)));
      toast('Slide added — you can generate the image from the Preview tab', 'success');
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to add slide';
      toast(message, 'error');
    }
  };

  const handleReorderMedia = async (orderedIds: number[]) => {
    if (!contentItem || !contentItem.media) return;
    // Optimistically reorder UI
    const idToMedia = Object.fromEntries((contentItem.media ?? []).map((m) => [m.id, m]));
    const reorderedMedia = orderedIds
      .map((id, idx) => ({ ...idToMedia[id], order: idx }))
      .filter(Boolean);
    const updatedItem = { ...contentItem, media: reorderedMedia };
    setContentItem(updatedItem);
    setContentItems((prev) => prev.map((i) => (i.id === updatedItem.id ? updatedItem : i)));
    try {
      await reorderMedia(orderedIds);
    } catch (err) {
      // Revert on failure by reloading
      const reloaded = await getContentItems();
      setContentItems(reloaded.results || []);
      const msg = err instanceof Error ? err.message : 'Failed to reorder slides';
      toast(msg, 'error');
    }
  };

  const handleUploadMediaFile = async (file: File, mediaId?: number) => {
    if (!contentItem) return;
    try {
      const uploaded = await uploadMediaFile(contentItem.id, file, {
        mediaId,
      });
      let updatedMedia: typeof contentItem.media;
      if (mediaId) {
        updatedMedia = (contentItem.media ?? []).map((m) =>
          m.id === mediaId ? { ...m, ...uploaded } : m
        );
      } else {
        updatedMedia = [...(contentItem.media ?? []), uploaded];
      }
      const updatedItem = { ...contentItem, media: updatedMedia };
      setContentItem(updatedItem);
      setContentItems((prev) => prev.map((i) => (i.id === updatedItem.id ? updatedItem : i)));
      toast('Image uploaded. AI generation will not overwrite your uploads.', 'success');
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to upload image';
      toast(message, 'error');
    }
  };

  /**
   * Derive the pipeline state from outline + content item data.
   * TODO (issue #108): replace with server-derived `workflow_status` field from
   * GET /api/content/workqueue/ once that endpoint ships.
   */
  const getPipelineState = (outline: ContentOutline, item?: ContentItem): PipelineState => {
    if (item?.status === 'published' || outline.status === 'published') return 'published';
    if (item?.status === 'scheduled' || outline.status === 'scheduled') return 'scheduled';
    if (item && hasMediaGenerated(item)) return 'ready_to_schedule';
    // Item generated but not yet approved by user → needs_item_approval
    if (item && (item.status === 'draft' || item.status === 'rejected'))
      return 'needs_item_approval';
    // Item exists and approved → needs media
    if (item) return 'needs_media';
    if (outline.status === 'approved' || outline.status === 'final') return 'needs_content';
    return 'needs_approval';
  };

  // Compute pipeline groups from loaded outlines
  // TODO (issue #108): replace with GET /api/content/workqueue/ response
  const enriched = outlines.map((o) => {
    const item = getContentItemForOutline(o.id);
    return { outline: o, item, state: getPipelineState(o, item) };
  });

  const groups = {
    needs_approval: enriched.filter((e) => e.state === 'needs_approval'),
    needs_item_approval: enriched.filter((e) => e.state === 'needs_item_approval'),
    needs_content: enriched.filter((e) => e.state === 'needs_content'),
    needs_media: enriched.filter((e) => e.state === 'needs_media'),
    ready_to_schedule: enriched.filter((e) => e.state === 'ready_to_schedule'),
    scheduled: enriched.filter((e) => e.state === 'scheduled'),
    published: enriched.filter((e) => e.state === 'published'),
  };

  /**
   * Pipeline health counts — computed client-side for now.
   * TODO (issue #109): replace with GET /api/content/pipeline-health/ response.
   */
  const healthCounts = {
    needsApproval: groups.needs_approval.length + groups.needs_item_approval.length,
    missingMedia: groups.needs_media.length,
    readyToPublish: groups.ready_to_schedule.length,
    scheduledThisWeek: groups.scheduled.length,
    autopilot: workspace.scheduler_enabled ?? false,
  };

  if (loading) {
    return (
      <div>
        <div className="page-header">
          <h1 className="page-header__title">Content</h1>
          <p className="page-header__subtitle">
            Your content pipeline — approve, produce, and schedule your posts.
          </p>
        </div>
        <LoadingOverlay label="Loading content…" />
      </div>
    );
  }

  if (selectedOutline) {
    const hasMedia = contentItem?.media && contentItem.media.length > 0;

    const handlePrevMedia = () => {
      if (hasMedia) {
        setCurrentMediaIndex((prev) => (prev === 0 ? contentItem.media!.length - 1 : prev - 1));
      }
    };

    const handleNextMedia = () => {
      if (hasMedia) {
        setCurrentMediaIndex((prev) => (prev === contentItem.media!.length - 1 ? 0 : prev + 1));
      }
    };

    return (
      <div>
        <div className="page-header">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => {
              setEditingCaption(false);
              setEditingHashtags(false);
              setEditingPromptId(null);
              setConfirmDelete(false);
              selectOutline(null);
            }}
            style={{ marginBottom: 12 }}
          >
            ← Back to Outlines
          </Button>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
            <h1 className="page-header__title" style={{ margin: 0 }}>
              {selectedOutline.title}
            </h1>
            <Badge variant={statusVariant(selectedOutline.status)} dot>
              Outline: {selectedOutline.status}
            </Badge>
            {selectedOutline.is_archived && <Badge variant="draft">Archived</Badge>}
            {contentItem && (
              <Badge variant={statusVariant(contentItem.status)} dot>
                Content: {contentItem.status}
              </Badge>
            )}
            <span
              style={{
                fontSize: 12,
                background: 'var(--bg-secondary)',
                borderRadius: 6,
                padding: '3px 8px',
                color: 'var(--text-secondary)',
                textTransform: 'uppercase',
                letterSpacing: '0.05em',
              }}
            >
              {selectedOutline.content_format}
            </span>
            <Button
              variant={selectedOutline.is_archived ? 'primary' : 'secondary'}
              size="sm"
              onClick={() =>
                selectedOutline.is_archived
                  ? void handleUnarchive(selectedOutline.id)
                  : void handleArchive(selectedOutline.id)
              }
            >
              {selectedOutline.is_archived ? 'Restore to Active' : 'Archive Post'}
            </Button>
          </div>
          {(selectedOutline.pillar || selectedOutline.plan) && (
            <p className="page-header__subtitle" style={{ marginTop: 6 }}>
              {selectedOutline.pillar && `Pillar: ${getPillarName(selectedOutline.pillar)}`}
              {selectedOutline.pillar && selectedOutline.plan && ' · '}
              {selectedOutline.plan && `Plan: ${getPlanName(selectedOutline.plan)}`}
              {selectedOutline.publish_at &&
                ` · Publishes ${formatDate(selectedOutline.publish_at)}`}
            </p>
          )}
        </div>

        {videoProgressBanner}

        {error && (
          <div className="page-error" role="alert">
            <span aria-hidden="true">⚠</span> {error}
          </div>
        )}

        {(() => {
          const mediaActive = hasActiveMediaJobForOutline(selectedOutline.id);
          const contentActive = hasActiveContentJobForOutline(selectedOutline.id);
          if (!mediaActive && !contentActive) return null;
          const kind: 'media' | 'content' = mediaActive ? 'media' : 'content';
          const startOverride = () =>
            setOverridePrompt({
              message: `Cancel the ${kind} generation already running for this post and start over from scratch?`,
              onConfirm: () => {
                setOverridePrompt(null);
                if (kind === 'media') {
                  if (!contentItem) return;
                  void handleGenerateMedia(contentItem.id, {
                    regenerateAll: true,
                    force: true,
                    generationModeOverride: generationMode,
                  });
                } else if (contentItem) {
                  void executeRegenerateContent(contentItem.id, '', true);
                } else {
                  void executeGenerateContent(selectedOutline.id, '', true);
                }
              },
            });
          return (
            <div className="cp-system-run-banner" role="status" aria-live="polite">
              <span className="cp-system-run-banner__icon" aria-hidden="true">
                ⚙️
              </span>
              <div className="cp-system-run-banner__body">
                <span className="cp-system-run-banner__title">
                  AI is generating {kind} for this post…
                </span>
                <span className="cp-system-run-banner__hint">
                  This may have been started automatically by the scheduler. The
                  buttons are locked so it isn&apos;t generated twice.
                </span>
              </div>
              <Button variant="secondary" size="sm" onClick={startOverride}>
                Override &amp; restart
              </Button>
            </div>
          );
        })()}

        <ContentDetailWorkspace
          outline={selectedOutline}
          contentItem={contentItem}
          pillars={pillars}
          plans={plans}
          allowedFormats={allowedFormats}
          initialTab={initialWorkspaceTab}
          currentMediaIndex={currentMediaIndex}
          onPrevMedia={handlePrevMedia}
          onNextMedia={handleNextMedia}
          onSetMediaIndex={setCurrentMediaIndex}
          generatingContent={generatingContent || hasActiveContentJobForOutline(selectedOutline.id)}
          generatingMediaIds={generatingMediaIds}
          multiSlideJobInFlight={
            multiSlideJobInFlight || hasActiveMediaJobForOutline(selectedOutline.id)
          }
          regeneratingAllMedia={regeneratingAllMedia}
          onGenerateContent={() => handleGenerateContentItem(selectedOutline.id)}
          onRegenerateContent={() => contentItem && handleRegenerateContentItem(contentItem.id)}
          onGenerateMedia={(options) =>
            contentItem &&
            handleGenerateMedia(contentItem.id, {
              ...options,
              contentFormat: contentItem.content_format,
            })
          }
          onApprove={() => handleApprove(selectedOutline.id)}
          onApproveContentItem={() => contentItem && void handleApproveContentItem(contentItem.id)}
          onOpenPublishModal={() => contentItem && handleOpenPublishModal(contentItem.id)}
          editingCaption={editingCaption}
          captionDraft={captionDraft}
          savingCaption={savingCaption}
          onStartEditCaption={() => {
            setCaptionDraft(contentItem?.caption || '');
            setEditingCaption(true);
          }}
          onCaptionDraftChange={setCaptionDraft}
          onSaveCaption={handleSaveCaption}
          onCancelEditCaption={() => setEditingCaption(false)}
          editingHashtags={editingHashtags}
          hashtagsDraft={hashtagsDraft}
          savingHashtags={savingHashtags}
          onStartEditHashtags={() => {
            setHashtagsDraft(contentItem?.hashtags || '');
            setEditingHashtags(true);
          }}
          onHashtagsDraftChange={setHashtagsDraft}
          onSaveHashtags={handleSaveHashtags}
          onCancelEditHashtags={() => setEditingHashtags(false)}
          editingPromptId={editingPromptId}
          promptDraft={promptDraft}
          savingPrompt={savingPrompt}
          onStartEditPrompt={(mediaId, prompt) => {
            setPromptDraft(prompt);
            setEditingPromptId(mediaId);
          }}
          onPromptDraftChange={setPromptDraft}
          onSavePrompt={(mediaId) => handleSaveMediaPrompt(mediaId, promptDraft)}
          onCancelEditPrompt={() => setEditingPromptId(null)}
          onToggleHideMedia={handleToggleHideMedia}
          onDeleteMedia={handleDeleteMedia}
          onAddMediaPrompt={handleAddMediaPrompt}
          onReorderMedia={handleReorderMedia}
          onUploadMediaFile={handleUploadMediaFile}
          confirmDelete={confirmDelete}
          onConfirmDelete={() => setConfirmDelete(true)}
          onCancelDelete={() => setConfirmDelete(false)}
          onDelete={() => handleDelete(selectedOutline.id)}
          formatDate={formatDate}
          getPillarName={getPillarName}
          getPlanName={getPlanName}
          schedulerTasks={schedulerTasks}
          savingOutlineMeta={savingOutlineMeta}
          onSaveOutlineMeta={handleSaveOutlineMeta}
          refreshingInstagramMetrics={refreshingInstagramMetrics}
          onRefreshInstagramMetrics={handleRefreshInstagramMetrics}
          workspace={workspace}
          onToggleCtaImage={handleToggleCtaImage}
          togglingCta={togglingCta}
          onGoToCtaSettings={() => onNavigate?.('settings')}
        />

        <ChatWidget workspaceId={workspaceId} />

        {/* Modals — rendered via createPortal so they appear above all content */}
        {notesModal && (
          <NotesModal
            title={notesModal.mode === 'generate' ? '✨ Generate Content' : '🔄 Regenerate Content'}
            subtitle={
              notesModal.mode === 'generate'
                ? 'AI will write the caption, hashtags, and image prompts for this outline.'
                : 'AI will rewrite the caption, hashtags, and image prompts from scratch.'
            }
            confirmLabel={notesModal.mode === 'generate' ? 'Generate' : 'Regenerate'}
            onConfirm={handleNotesConfirm}
            onClose={() => setNotesModal(null)}
          />
        )}

        <ConfirmModal
          isOpen={Boolean(overridePrompt)}
          title="Already generating"
          confirmLabel="Override & restart"
          confirmVariant="danger"
          onConfirm={() => overridePrompt?.onConfirm()}
          onClose={() => setOverridePrompt(null)}
        >
          {overridePrompt?.message}
        </ConfirmModal>

        {showPublishModal && contentItem && (
          <PublishModal
            workspace={workspace}
            contentTitle={selectedOutline.title}
            onClose={() => setShowPublishModal(false)}
            onPublish={handlePublish}
            onMarkManuallyUploaded={handleMarkManuallyUploaded}
            onGoToSettings={handleGoToSettings}
          />
        )}

        {carouselModeModal && (
          <CarouselGenerationModeModal
            defaultMode={generationMode}
            onClose={() => setCarouselModeModal(null)}
            onConfirm={(mode) => {
              setGenerationMode(mode);
              const pending = carouselModeModal;
              setCarouselModeModal(null);
              void handleGenerateMedia(pending.contentItemId, {
                ...pending.options,
                generationModeOverride: mode,
              });
            }}
          />
        )}

        {carouselRegenModal && contentItem && (
          <CarouselRegenerateModal
            defaultMode={generationMode}
            totalSlides={contentItem.media?.length ?? 0}
            currentSlideIndex={currentMediaIndex}
            onClose={() => setCarouselRegenModal(null)}
            onConfirm={({ scope, mode }) => {
              const pending = carouselRegenModal;
              setCarouselRegenModal(null);
              if (scope === 'all') {
                setGenerationMode(mode);
                void handleGenerateMedia(pending.contentItemId, {
                  regenerateAll: true,
                  contentFormat: 'carousel',
                  generationModeOverride: mode,
                });
              } else {
                const currentMedia = contentItem.media?.[currentMediaIndex];
                if (!currentMedia) return;
                void handleGenerateMedia(pending.contentItemId, {
                  mediaIndex: currentMediaIndex,
                  mediaId: currentMedia.id,
                  contentFormat: 'carousel',
                });
              }
            }}
          />
        )}
      </div>
    );
  }

  // ── Queue section renderer ─────────────────────────────────────────────────
  type QueueColor = 'orange' | 'teal' | 'green' | 'gray';

  const renderQueueSection = (
    title: string,
    icon: string,
    description: string,
    items: Array<{ outline: ContentOutline; item: ContentItem | undefined; state: PipelineState }>,
    color: QueueColor,
    defaultCollapsed = false
  ) => {
    if (items.length === 0 && defaultCollapsed) return null;

    const countClass = `cp-queue-header__count--${color}`;

    return (
      <div className="cp-queue-section" key={title}>
        <div className="cp-queue-header">
          <span className="cp-queue-header__icon">{icon}</span>
          <span className="cp-queue-header__title">
            {title}
            {description && <span className="cp-queue-header__description"> — {description}</span>}
          </span>
          <span className={`cp-queue-header__count ${countClass}`}>{items.length}</span>
        </div>

        {items.length === 0 ? (
          <div className="cp-queue-empty">Nothing here — you're all caught up ✓</div>
        ) : (
          items.map(({ outline, item, state }) => (
            <QueueItem
              key={outline.id}
              outline={outline}
              item={item}
              state={state}
              generatingContent={generatingContent}
              multiSlideJobInFlight={multiSlideJobInFlight}
              generatingMediaIds={generatingMediaIds}
              videoJobStatus={videoJobStatus}
              hasActiveContentJobForOutline={hasActiveContentJobForOutline}
              hasActiveMediaJobForOutline={hasActiveMediaJobForOutline}
              handleApprove={handleApprove}
              handleApproveContentItem={handleApproveContentItem}
              handleGenerateContentItem={handleGenerateContentItem}
              handleGenerateMedia={handleGenerateMedia}
              handleOpenPublishModal={handleOpenPublishModal}
              selectOutline={selectOutline}
              getPillarName={getPillarName}
              formatDate={formatDate}
              handleUnarchive={handleUnarchive}
              handleArchive={handleArchive}
            />
          ))
        )}
      </div>
    );
  };

  return (
    <div className="content-pipeline">
      {/* ── Header ──────────────────────────────────────────────────────────── */}
      <div className="content-pipeline__header">
        <div>
          <h1 className="content-pipeline__title">Content</h1>
          <p className="content-pipeline__subtitle">
            Your content pipeline — approve, produce, and schedule your posts.
          </p>
        </div>
        <div className="content-pipeline__header-actions">
          <Button variant="secondary" onClick={() => setShowReelStudio(true)}>
            🎞 Reel Studio
          </Button>
          <Button variant="primary" onClick={() => openCreationModal()}>
            + New Outline
          </Button>
        </div>
      </div>

      <ContentTabs
        currentTab={currentTab}
        activeCount={activeCount}
        archivedCount={archivedCount}
        setCurrentTab={setCurrentTab}
      />

      {/* ── Error banner ────────────────────────────────────────────────────── */}
      {error && (
        <div className="page-error" role="alert">
          <span aria-hidden="true">⚠</span> {error}
        </div>
      )}

      {/* ── AI generating indicator ─────────────────────────────────────────── */}
      {(generating ||
        generatingContent ||
        activeJobs.some((j) => CONTENT_JOB_ACTIONS.has(j.action))) && (
        <GenerationProgress generating={generating} />
      )}

      {videoProgressBanner}

      {carouselModeModal && (
        <CarouselGenerationModeModal
          defaultMode={generationMode}
          onClose={() => setCarouselModeModal(null)}
          onConfirm={(mode) => {
            setGenerationMode(mode);
            const pending = carouselModeModal;
            setCarouselModeModal(null);
            void handleGenerateMedia(pending.contentItemId, {
              ...pending.options,
              generationModeOverride: mode,
            });
          }}
        />
      )}

      {/* ── Agent message banner ─────────────────────────────────────────────── */}
      {agentMessage && (
        <AgentBanner
          agentMessage={agentMessage}
          agentMessageType={agentMessageType}
          setAgentMessage={setAgentMessage}
        />
      )}

      {/* ── Pipeline health strip ─────────────────────────────────────────────
          TODO (issue #109): drive counts from GET /api/content/pipeline-health/
          ──────────────────────────────────────────────────────────────────── */}
      {outlines.length > 0 && currentTab === 'active' && (
        <PipelineHealthCard healthCounts={healthCounts} />
      )}

      {/* ── Empty state (no outlines at all) ─────────────────────────────────── */}
      {outlines.length === 0 && !generating && currentTab === 'active' && (
        <div className="cp-empty-state">
          <div className="cp-empty-state__emoji">✨</div>
          <h2 className="cp-empty-state__title">Your content pipeline is empty</h2>
          <p className="cp-empty-state__subtitle">
            Start by generating post ideas with AI — or create your first outline manually. Every
            post starts here.
          </p>
          <div className="cp-empty-state__actions">
            <button className="cp-empty-btn--primary" onClick={() => openCreationModal('quick')}>
              ✨ Generate with AI
            </button>
            <button className="cp-empty-btn--secondary" onClick={() => openCreationModal('manual')}>
              ✏️ Create manually
            </button>
          </div>
        </div>
      )}

      {outlines.length === 0 && !generating && currentTab === 'archived' && (
        <div className="cp-empty-state cp-empty-state--archived">
          <div className="cp-empty-state__emoji">🗂️</div>
          <h2 className="cp-empty-state__title">No archived posts yet</h2>
          <p className="cp-empty-state__subtitle">
            Archive posts from Active when you want a cleaner pipeline while keeping history.
          </p>
          <div className="cp-empty-state__actions">
            <button className="cp-empty-btn--secondary" onClick={() => setCurrentTab('active')}>
              Go to Active
            </button>
          </div>
        </div>
      )}

      {/* ── Queue sections ───────────────────────────────────────────────────────
          TODO (issue #108): drive sections from GET /api/content/workqueue/
          ──────────────────────────────────────────────────────────────────── */}
      {outlines.length > 0 && currentTab === 'active' && (
        <>
          {renderQueueSection(
            'Needs Your Decision',
            '⚡',
            'approve these to move them forward',
            [...groups.needs_approval, ...groups.needs_item_approval],
            'orange'
          )}
          {renderQueueSection(
            'Waiting for Content',
            '🤖',
            'trigger AI to write captions & prompts',
            groups.needs_content,
            'teal'
          )}
          {renderQueueSection(
            'Missing Media',
            '🎨',
            'generate images or video for these posts',
            groups.needs_media,
            'teal'
          )}
          {renderQueueSection(
            'Ready to Publish',
            '✅',
            'upload or schedule these now',
            groups.ready_to_schedule,
            'green'
          )}
          {renderQueueSection(
            'Scheduled',
            '📅',
            'queued to go live',
            groups.scheduled,
            'gray',
            true
          )}
          {renderQueueSection(
            'Published',
            '📤',
            'live on Instagram',
            groups.published,
            'gray',
            true
          )}
        </>
      )}

      {outlines.length > 0 && currentTab === 'archived' && (
        <>
          {renderQueueSection(
            'Archived Posts',
            '🗂️',
            'kept for reference, hidden from your active pipeline',
            enriched,
            'gray'
          )}
        </>
      )}

      {/* ── Pagination ───────────────────────────────────────────────────────── */}
      {totalCount > pageSize && (
        <div className="cp-pagination">
          <Button
            variant="secondary"
            size="sm"
            onClick={() => setCurrentPage((prev) => Math.max(1, prev - 1))}
            disabled={currentPage === 1}
          >
            ← Previous
          </Button>
          <span className="cp-pagination__info">
            Page {currentPage} of {Math.ceil(totalCount / pageSize)}
          </span>
          <Button
            variant="secondary"
            size="sm"
            onClick={() => setCurrentPage((prev) => prev + 1)}
            disabled={currentPage >= Math.ceil(totalCount / pageSize)}
          >
            Next →
          </Button>
        </div>
      )}

      {/* ── Modals ───────────────────────────────────────────────────────────── */}
      {notesModal && (
        <NotesModal
          title={notesModal.mode === 'generate' ? '✨ Generate Content' : '🔄 Regenerate Content'}
          subtitle={
            notesModal.mode === 'generate'
              ? 'AI will write the caption, hashtags, and image prompts for this outline.'
              : 'AI will rewrite the caption, hashtags, and image prompts from scratch.'
          }
          confirmLabel={notesModal.mode === 'generate' ? 'Generate' : 'Regenerate'}
          onConfirm={handleNotesConfirm}
          onClose={() => setNotesModal(null)}
        />
      )}

      <ConfirmModal
        isOpen={Boolean(overridePrompt)}
        title="Already generating"
        confirmLabel="Override & restart"
        confirmVariant="danger"
        onConfirm={() => overridePrompt?.onConfirm()}
        onClose={() => setOverridePrompt(null)}
      >
        {overridePrompt?.message}
      </ConfirmModal>

      {showReelStudio && (
        <ReelStudioModal
          workspaceId={workspaceId}
          onClose={() => setShowReelStudio(false)}
          onCreated={() => {
            void loadData();
          }}
        />
      )}

      {showCreationModal && (
        <OutlineCreationModal
          pillars={pillars}
          plans={plans}
          allowedFormats={allowedFormats}
          initialMode={creationModalInitialMode}
          autoApprove={workspace.auto_approve}
          onClose={() => {
            if (!generating) {
              setShowCreationModal(false);
              setCreationModalInitialMode('quick');
            }
          }}
          onCreateOwn={async (data) => {
            await handleCreateOwn(data);
            setShowCreationModal(false);
            setCreationModalInitialMode('quick');
          }}
          onCreateWithAI={async (data) => {
            await handleCreateWithAI(data);
          }}
          loading={generating}
        />
      )}

      {showPublishModal &&
        publishingContentId &&
        (() => {
          const publishItem = contentItems.find((i) => i.id === publishingContentId);
          const publishOutline = publishItem
            ? outlines.find((o) => o.id === publishItem.outline)
            : null;
          return publishItem && publishOutline ? (
            <PublishModal
              workspace={workspace}
              contentTitle={publishOutline.title}
              onClose={() => setShowPublishModal(false)}
              onPublish={handlePublish}
              onMarkManuallyUploaded={handleMarkManuallyUploaded}
              onGoToSettings={handleGoToSettings}
            />
          ) : null;
        })()}

      <ChatWidget workspaceId={workspaceId} />
    </div>
  );
}

export default ContentPage;
